import React, { useState } from 'react';
import { Alert, Button, Input, Modal } from 'antd';
import { CheckOutlined, CopyOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

type Props = {
  title: string;
  path: string;
  // Akış kartı gibi başka içerik paylaşımında: paylaşım metni, düğme adı ve sınıfı.
  text?: string;
  label?: string;
  className?: string;
  // Paylaşım menüsü tamamlandı ya da bağlantı kopyalandı (ölçüm için).
  onShared?: () => void;
  // Simge yerine etiketli buton (ör. maç penceresinde "Sonucu paylaş").
  showLabel?: boolean;
};

/** Cihazın paylaşım menüsü (WhatsApp, Instagram…); yoksa bağlantıyı kopyalama penceresi. */
export default function LeagueShareButton({
  title,
  path,
  text,
  label,
  className,
  onShared,
  showLabel,
}: Props) {
  const { t } = useTranslation();
  const name = label || t('leagues:detail.share.label');
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const [link, setLink] = useState('');

  const share = async () => {
    const url = new URL(path, window.location.origin).href;
    setLink(url);
    setCopied(false);
    setCopyError(false);
    if (navigator.share) {
      setBusy(true);
      try {
        await navigator.share(text ? { title, text, url } : { title, url });
        onShared?.();
        return;
      } catch (error) {
        if ((error as Error).name === 'AbortError') return;
      } finally {
        setBusy(false);
      }
    }
    setVisible(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      onShared?.();
      setCopyError(false);
    } catch {
      setCopyError(true);
    }
  };

  return (
    <>
      <Button
        type={showLabel ? 'default' : 'text'}
        className={className || 'league-share-button'}
        icon={<ShareAltOutlined aria-hidden="true" />}
        aria-label={showLabel ? undefined : name}
        title={name}
        aria-busy={busy}
        onClick={share}
        loading={busy}
      >
        {showLabel ? name : null}
      </Button>
      <Modal
        className="league-share-modal"
        title={name}
        visible={visible}
        onCancel={() => setVisible(false)}
        footer={null}
      >
        <div className="league-share-copy">
          {copyError && <Alert role="alert" type="error" message={t('common:widgets.copyError')} />}
          <Input
            readOnly
            value={link}
            aria-label={t('common:widgets.shareLink')}
            onFocus={event => event.currentTarget.select()}
          />
          <Button onClick={copy} icon={copied ? <CheckOutlined /> : <CopyOutlined />}>
            <span role="status">{t(copied ? 'common:widgets.copied' : 'common:widgets.copy')}</span>
          </Button>
        </div>
      </Modal>
    </>
  );
}

LeagueShareButton.defaultProps = {
  text: undefined,
  label: undefined,
  className: undefined,
  onShared: undefined,
  showLabel: false,
};
